import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { currentMonth, type MonthKey } from '@horof/shared';
import { Button, DatePicker, Segmented } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { monthRange, nextMonth, previousMonth, wholeMonth } from './dates';

type Mode = 'month' | 'range' | 'all';

/**
 * The operations' period: a month (with ‹ › to step through them), any range
 * of days, or everything. Written as `from` / `to` (YYYY-MM-DD).
 */
export function PeriodFilter({
  from,
  to,
  onChange,
}: {
  from: string | null;
  to: string | null;
  onChange: (from: string | null, to: string | null) => void;
}) {
  const month = wholeMonth(from, to);
  const [mode, setMode] = useState<Mode>(
    month ? 'month' : from || to ? 'range' : 'all',
  );
  const setMonth = (m: MonthKey) => onChange(...monthRange(m));

  return (
    <span className="desk-period">
      <Segmented<Mode>
        value={mode}
        onChange={(next) => {
          setMode(next);
          if (next === 'all') onChange(null, null);
          // A month: the one of the range, else this month.
          if (next === 'month' && !month) {
            setMonth(from ? (from.slice(0, 7) as MonthKey) : currentMonth());
          }
        }}
        options={[
          { value: 'month', label: 'شهر' },
          { value: 'range', label: 'فترة' },
          { value: 'all', label: 'الكل' },
        ]}
      />
      {mode === 'month' && (
        <span className="desk-month-picker">
          <Button
            icon={<RightOutlined />}
            title="الشهر السابق"
            aria-label="الشهر السابق"
            disabled={!month}
            onClick={() => month && setMonth(previousMonth(month))}
          />
          <DatePicker
            picker="month"
            allowClear={false}
            format="MMMM YYYY"
            placeholder="اختر الشهر"
            value={month ? dayjs(`${month}-01`) : null}
            onChange={(d) => d && setMonth(d.format('YYYY-MM') as MonthKey)}
          />
          <Button
            icon={<LeftOutlined />}
            title="الشهر التالي"
            aria-label="الشهر التالي"
            disabled={!month}
            onClick={() => month && setMonth(nextMonth(month))}
          />
        </span>
      )}
      {mode === 'range' && (
        <DatePicker.RangePicker
          format="YYYY-MM-DD"
          placeholder={['من', 'إلى']}
          allowEmpty={[true, true]}
          value={[from ? dayjs(from) : null, to ? dayjs(to) : null]}
          onChange={(range) =>
            onChange(
              range?.[0]?.format('YYYY-MM-DD') ?? null,
              range?.[1]?.format('YYYY-MM-DD') ?? null,
            )
          }
          presets={[
            {
              label: 'هذا الشهر',
              value: [dayjs().startOf('month'), dayjs().endOf('month')],
            },
            {
              label: 'الشهر الماضي',
              value: [
                dayjs().subtract(1, 'month').startOf('month'),
                dayjs().subtract(1, 'month').endOf('month'),
              ],
            },
            {
              label: 'آخر 3 أشهر',
              value: [
                dayjs().subtract(2, 'month').startOf('month'),
                dayjs().endOf('month'),
              ],
            },
            {
              label: 'هذه السنة',
              value: [dayjs().startOf('year'), dayjs().endOf('year')],
            },
            {
              label: 'السنة الماضية',
              value: [
                dayjs().subtract(1, 'year').startOf('year'),
                dayjs().subtract(1, 'year').endOf('year'),
              ],
            },
          ]}
        />
      )}
    </span>
  );
}
